import { IInitRequestPacket, IInitResponsePacket, ReturnCode, SignatureByte } from 'app/modules/remote-control-ng/definitions';
import { IDeviceContextHandler, IDeviceInput, InstallParameters, IRcAppUI, IDeviceScreenTransformService, IDeviceCanvasRender } from '.';
import { RcContext } from '../remote-control-ng/context/rcContext';
import { IdleTimerService } from '../remote-control-ng/services/idleTimerService';
import { ConnectionBridge } from '../remote-control-ng/services/connectionBridge';
import { GetSysInfoMessage, LoginMessage, PingMessage, RestartMessage } from "../remote-control-ng/messages/pocket-com";
import { RcLoggerService } from "../remote-control-ng/services/rcLoggerService";
import { RcFileSystemService } from "../remote-control-ng/context/fileSystem";
import { AppUIService } from "../remote-control-ng/services/appUIService";
import { RestartThrottling } from "../remote-control-ng/services/restartThrottling";
import { BaseMessage } from "./messages";
import { Observable, Subscription } from 'rxjs';
import { DeviceSkinController } from '../remote-control-ng/services/deviceSkinController';
import { StartRemoteControlMessage } from './messages/com/startRemoteControlMessage';
import { DeviceContext, IRcDeviceContext, RemoteControlStreamingContext } from '../remote-control-ng/context/deviceContext';
import { FeatureToggleService, hd } from '@nx-fe/services';
import { DeviceInfo, NgRemoteControlConnection } from '../remote-control/services/remote-control.connection';
import { BaseSocket } from './webSocket';
import { TimerWorkerService } from './services/timer.worker.service';

interface IInitState {
    deviceModel: string;
    initResponse: IInitResponsePacket;
}

export class Session {
    private deviceModel: string = "";
    private deviceSkinController: DeviceSkinController = null;
    private _isPaused: boolean = false;
    public get isPaused() {
        return this._isPaused;
    }
    public resetPause = () => this._isPaused = false;
    private pingService: TimerWorkerService;
    private pingInterval$: Subscription;
    private idleTimer: IdleTimerService;
    public initResponsePacket: IInitResponsePacket = null;
    private zoom: number = 100;

    private deviceCanvas: IDeviceCanvasRender;
    private deviceInput: IDeviceInput;
    private transformService: IDeviceScreenTransformService;
    private ctx: IRcDeviceContext;
    private deviceContextHandler: IDeviceContextHandler;
    private connection: ConnectionBridge;

    private mcrcLog: RcLoggerService;
    private fileSystem: RcFileSystemService;
    private restartThrottle: RestartThrottling;
    private featureToggle: FeatureToggleService;
    private _appUI: AppUIService;
    public get appUI(): IRcAppUI {
        return this._appUI;
    }
    constructor() {
        this.connection = RcContext.instance.getService(ConnectionBridge);
        // this.mcrcLog = RcContext.instance.getService(RcLoggerService);
        this.fileSystem = RcContext.instance.getService(RcFileSystemService);
        this.restartThrottle = RcContext.instance.getService(RestartThrottling);
        this._appUI = RcContext.instance.getService(AppUIService);
        this.featureToggle = RcContext.instance.getService(FeatureToggleService);
        this.pingService = RcContext.instance.getService(TimerWorkerService);
    }

    /** Session gets constructed early to provide access the ConnectionBridge to the rest of the application.
     * Only later in the process it is initialized with the canvas, context and services required
     */
    public init(
        deviceCanvas: IDeviceCanvasRender,
        deviceInput: IDeviceInput,
        transformService: IDeviceScreenTransformService,
        ctx: IRcDeviceContext,
        deviceContextHandler: IDeviceContextHandler,
        initPacket?: IInitRequestPacket,
    ) {
        this.deviceCanvas = deviceCanvas;
        this.deviceInput = deviceInput;
        this.transformService = transformService;
        this.ctx = ctx;
        this.deviceContextHandler = deviceContextHandler;
        this._appUI.init(this.deviceCanvas);
        if (initPacket != null) {
            this.sendInitSessionRequestMessage(initPacket, SignatureByte.SignatureCommMessage);
        }
    }

    // convert model name to skin name
    private modelToSkinName = (model: string) => {
        // when model is "", it should not return "null"
        if (model === undefined || model == null) {
            return "null";
        }

        // Remove duplicated dots for Inc. and Ltd.
        model = model.replace(/\.\.+/g, '.');
        // Remove spaces
        model = model.replace(/\s/g, '').replace('+', '');
        const index = model.indexOf(".");
        const indexEnd = model.indexOf(".", index + 1);
        return model.substr(0, indexEnd > -1 ? indexEnd : model.length);
    }

    private initScreenDimensions = (initResponse: IInitResponsePacket): void => {
        if (initResponse.width && initResponse.height) {
            if (initResponse.rotation != 0 && initResponse.rotation != 2) {
                this.ctx.deviceHeight = initResponse.width;
                this.ctx.deviceWidth = initResponse.height;
            } else {
                this.ctx.deviceHeight = initResponse.height;
                this.ctx.deviceWidth = initResponse.width;
            }
            this.ctx.screenWidth = this.ctx.deviceWidth;
            this.ctx.screenHeight = this.ctx.deviceHeight;
        }
    };

    private initModel(initResponse: IInitResponsePacket): void {
        // HACK: For Universal Windows devices (osType == 40000), until MobiControl stops explicitly disabling skins for them
        if (initResponse.osType == 40000) {
            RcContext.instance.skinName = undefined;
        }

        if (RcContext.instance.skinName !== '' && RcContext.instance.skinName !== undefined) {
            this.deviceModel = RcContext.instance.skinName;
            if (this.deviceModel === null) {
                this.deviceModel = '';
            }
        } else if (initResponse.hasModelInfo) {
            this.deviceModel = this.modelToSkinName(initResponse.model);
        }

        if (initResponse.rotation >= 0) {
            (<DeviceContext>RcContext.instance.deviceContext).setRotation(initResponse.rotation);
        }
    };

    private initInput = (): void => {
        this.deviceInput.registerMouseInputListeners(this.ctx.canvas.element);
        this.deviceInput.registerKeyInputListeners();
    };

    private sessionEstablished(): void {
        this.initInput();

        const currentSkinModel = RcContext.instance.multiScreen.requestMonitorId !== 0 ? "" : this.deviceModel;
        this.deviceSkinController = new DeviceSkinController(
            currentSkinModel,
            this.transformService.updateSkin,
            this.ctx as DeviceContext,
            this.deviceContextHandler,
        );
        this.deviceInput.setSkinController(this.deviceSkinController);

        let evt = new Event('rcSessionEstablished', { bubbles: true, cancelable: true });
        evt = Object.assign(evt, { deviceId: RcContext.instance.deviceId });
        document.dispatchEvent(evt);
    };

    // TODO NT: Remove this method once DeviceContextHandler is in place
    private mergeInitOrLoginResponses(oldMessage: IInitResponsePacket, newMessage: IInitResponsePacket): IInitResponsePacket {

        oldMessage.permissions = newMessage.permissions ? newMessage.permissions : oldMessage.permissions;
        oldMessage.returnCode = newMessage.returnCode ?? oldMessage.returnCode;
        oldMessage.versionHigh = newMessage.versionHigh ? newMessage.versionHigh : oldMessage.versionHigh;
        oldMessage.versionLow = newMessage.versionLow ? newMessage.versionLow : oldMessage.versionLow;

        if (newMessage.hasModelInfo) {
            oldMessage.hasModelInfo = newMessage.hasModelInfo;
            oldMessage.osType = newMessage.osType;
            oldMessage.model = newMessage.model;
        }

        if (newMessage.hasScreenInfo) {
            oldMessage.hasScreenInfo = newMessage.hasScreenInfo;
            oldMessage.width = newMessage.width;
            oldMessage.height = newMessage.height;
            oldMessage.rotation = newMessage.rotation;
        }

        return oldMessage;
    };

    //
    // Public Members
    //
    public processInitResponse = (initResponse: IInitResponsePacket): number => {
        // extension to the init deserializer
        this.initResponsePacket = this.initResponsePacket ? this.mergeInitOrLoginResponses(this.initResponsePacket, initResponse) : initResponse;

        this.connection.setLastError(initResponse.returnCode);

        // // [HD-1016]: the permissions are sent by PC_INIT response, even if returnCode == PCR_LOGIN_REQUIRED
        if (initResponse.permissions !== undefined) {
            // SecurityContext.All permissions uses only 48 bits precision, so converting
            // BigInteger to Number is safe as long as All uses 53 bits or less
            RcContext.instance.security.initialize(Number(initResponse.permissions));
        }

        if (initResponse.versionHigh !== undefined) {
            RcContext.instance.versionHi = initResponse.versionHigh;
            RcContext.instance.versionLo = initResponse.versionLow;
        }

        if (initResponse.returnCode === ReturnCode.PCR_OK) {
            RcContext.instance.deviceContext.osType = initResponse.osType;
            if (initResponse.hasScreenInfo) {
                this.initScreenDimensions(initResponse);
            }

            this.initModel(initResponse);

            this.sessionEstablished();
        }

        return initResponse.returnCode;
    };

    public processStreamingInitResponse = (response: Partial<IInitResponsePacket>): void => {
        const deviceContext = RcContext.instance.deviceContext as RemoteControlStreamingContext;
        const streamingConnection = deviceContext.remoteControlConnection as NgRemoteControlConnection;
        if (response.permissions) {
            if (deviceContext.device.isRemoteViewOnly) {
                response.permissions = response.permissions.and(~hd.RemoteControlPermissions.RemoteControlInput);
            }

            deviceContext.permissions = response.permissions.valueOf();
            RcContext.instance.security.initialize(response.permissions.valueOf());
        }

        if (response.returnCode === ReturnCode.PCR_LOGIN_REQUIRED) {
            this.fireEvent('remoteControlLogin');
            const message = new LoginMessage(RcContext.instance.userName);
            RcContext.instance.session.sendRequest(message);
        } else if (response.returnCode !== ReturnCode.PCR_OK) {
            console.error(`Error ${response.returnCode} starting remote control`);
            this.connection.setLastError(response.returnCode);

            if (streamingConnection.onStatusUpdate) {
                switch (response.returnCode) {
                    case 3:
                        deviceContext.deviceInfo.status = DeviceInfo.INCOMPATIBLE; // wrong version
                        break;
                    case 6:
                        deviceContext.deviceInfo.status = DeviceInfo.REJECTED; // login rejected
                        break;
                    case 10:
                        // handle DS bug that sending error code 10 in WebRemoteControlHandler.Link() when forceOverride is true
                        if (RcContext.instance.forceOverride) {
                            console.log('forceOverride is true, ignore error');
                            return;
                        }

                        deviceContext.deviceInfo.status = DeviceInfo.BUSY; // already connected
                        break;
                    default:
                        deviceContext.deviceInfo.status = DeviceInfo.UNKNOWN_ERROR;
                        break;
                }

                deviceContext.deviceInfo.message = `Error ${response.returnCode} starting remote control`;
                streamingConnection.onStatusUpdate(deviceContext.deviceInfo);

                // this.closeConnection()
                this.close();
            }
        } else {
            streamingConnection.onRcSessionEstablished();

            // send or schedule sending pending PC_RESTART
            this.restartThrottle.sendRestart(RcContext.instance);

            deviceContext.osType = response.osType;
            deviceContext.rotation = response.rotation;
            const defaultRotation = response.rotation % 2 === 0;
            deviceContext.screenWidth = defaultRotation ? response.width : response.height;
            deviceContext.screenHeight = defaultRotation ? response.height : response.width;

            if (streamingConnection.onStatusUpdate) {
                deviceContext.deviceInfo.status = DeviceInfo.HANDSHAKED;
                deviceContext.deviceInfo.modelName = response.model ? this.modelToSkinName(response.model) : null;
                streamingConnection.onStatusUpdate(deviceContext.deviceInfo);
            }

            // work with mcrc -- see mcrc.com.Session.processInitResponse()
            if (!response.versionHigh || !response.versionLow) {
                response.versionHigh = deviceContext.remoteControlSharedData.data['AgentVersionHigh'];
                response.versionLow = deviceContext.remoteControlSharedData.data['AgentVersionLow'];
            }

            RcContext.instance.session.appUI.setOsType(response.osType);
            RcContext.instance.versionHi = response.versionHigh;
            RcContext.instance.versionLo = response.versionLow;

            const noActivityTimeout = Number(deviceContext.remoteControlSharedData.data['NoActivityTimeout']);
            if (noActivityTimeout > 0) {
                this.startIdleTimer(noActivityTimeout); // no need to setup idle timer for popout window as the original timer is always working
            }
        }
    }

    private fireEvent = (eventName: string, doc?: Document): void => {
        if (!doc) {
            doc = document;
        }

        const evt = doc.createEvent('HTMLEvents');
        evt.initEvent(eventName, true, true);
        const evtExt = evt as (Event & { deviceId: string });
        evtExt.deviceId = RcContext.instance.deviceId;
        doc.dispatchEvent(evtExt);
    };

    public transfer = (oldSession: Session): void => {
        const oldState = oldSession.getState();

        // Transfer necessary state from previous session
        this.deviceModel = oldState.deviceModel;

        if (oldSession.clearIdleTimer()) {
            this.startIdleTimer();
        }

        // Need to re-process init to ensure rc state is setup properly for new mcrc
        if (oldState.initResponse) {
            this.processInitResponse(oldState.initResponse);

            if (this.transformService.isAutoZoomActive()) {
                this.transformService.autoZoomToCurrentViewSize();
            }
            if (this.featureToggle.isEnabled("RemoteControlTransferCanvas")) {
                // Setting _isPaused to false will prevent a restart from being sent after the first screen packet.
                // When transferring the rc canvas buffer, we want to prevent this for optimization purposes.
                this._isPaused = false;
            } else {
                this._isPaused = true;
            }

            this.startPing();
        }
    };

    public getState = (): IInitState => {
        return {
            deviceModel: this.deviceModel,
            initResponse: this.initResponsePacket
        };
    };

    public start() {
        if (this._isPaused) {
            this.restart({}, true);
            this._isPaused = false;
        }

        this.startIdleTimer();
        this.startPing();
        this.sendRequest(new GetSysInfoMessage());
    }

    private clearIdleTimer(): boolean {
        if (this.idleTimer) {
            this.idleTimer?.clearTimer();
            this.idleTimer = null;
            document.removeEventListener('click', this.idleTimerClick);
            return true;
        }
        return false;
    }

    private startIdleTimer(noActivityTimeout?: number) {
        // Add a click event handler to trigger the custom event
        this.idleTimer = new IdleTimerService();
        document.addEventListener('click', this.idleTimerClick);
        this.idleTimer.timeout$.subscribe(() => {
            this.connection.setLastError(ReturnCode.PCR_TIMEOUT);
            this.close();
        });
        const idleTimeout = !!noActivityTimeout ? noActivityTimeout : RcContext.instance.noActivityTimeout;
        this.idleTimer.init(idleTimeout);
    }

    private idleTimerClick = () => this.idleTimer?.clickEvent();

    public login() {
        const evt = new Event('remoteControlLogin', { bubbles: true, cancelable: true });
        const evtExt = evt as (Event & { deviceId: string });
        evtExt.deviceId = RcContext.instance.deviceId;
        document.dispatchEvent(evtExt);

        this.sendRequest(new LoginMessage(RcContext.instance.userName));
    }

    public close = () => {
        this.connection.close();
        this.stopPing();
        this.clearIdleTimer();
    }

    public isConnected = (): boolean => this.connection.isConnected();


    public stopPing = () => {
        if (this.pingInterval$) {
            this.pingService.stop('ping');
            this.pingInterval$.unsubscribe();
            this.pingInterval$ = null;
        }
    }

    public startPing = () => {
        this.stopPing();
        this.pingInterval$ = this.pingService.start('ping', 30000).subscribe(() => {
            this.connection.send(new PingMessage());
        });
    }

    public pause = (): void => {
        this._isPaused = true;
        this._appUI.displayInProgressScreen();
        this.fileSystem.pause();
    }

    public sendMessage = (msg: BaseMessage) => this.connection.send(msg);

    /** Used when expecting a reply, returning am Observable.
     * Replies to these requests should be processed with *processReply*
     */
    public sendRequest = (msg: BaseMessage): Observable<BaseMessage> => this.connection.sendRequest(msg);

    /** Used when the application uses callbacks. Eventually, when file commands and task commands are
     * merged with the thin converted services calling them, most of these calls should be moved
     * to the observable version of the sendMessage. We will maintain callbacks and this function in 2024.0.4.
     * **IMPORTANT**: replies to requests sent with the sendCallbackRequest should be processed with *processCallback*
     * **AND NOT WITH** *processReply*
     */
    public sendCallbackRequest = (msg: BaseMessage,
        callback?: (...args: any[]) => void,
        fallback?: (...args: any[]) => void): number => this.connection.sendCallbackRequest(msg, callback, fallback);

    /** @deprecated - not used in 2024 */
    public clearRCPause = () => this._isPaused = false;

    public createConnection = (settings: InstallParameters) => this.connection.init(settings);

    public restart(options: Partial<RestartMessage>, force: boolean): void {
        this.restartThrottle.send(options.scale, force, RcContext.instance);
    }

    public hasStarted = (): boolean => this.initResponsePacket != null;

    public constructInitKeys = (
        recordingService: boolean,
        scale: number,
        ticketId?: number | string,
        deviceName?: string,
        fps?: number,
        proxy?: string,
    ): string => ConnectionBridge.constructInitKeyString(recordingService, scale, ticketId, deviceName, fps, proxy);

    private sendInitSessionRequestMessage = (initPacket: IInitRequestPacket,
        protocol?: SignatureByte.SignatureCommMessage
    ): void => {
        // this.mcrcLog.info("Sending PC_INIT...");
        const message = new StartRemoteControlMessage(initPacket, protocol);
        if (this.isConnected()) {
            this.sendMessage(message);
            // this.registerThrottler();
        }
        else {
            this.connection.subscribeOpenHandler(() => {
                this.connection.unsubscribeOpenHandler();
                this.sendMessage(message);
                // this.registerThrottler();
            });
        }

    }

    public setLastError = (error: number) => this.connection.setLastError(error);

    public updateDeviceSkinController = (id: number, onSkinLoaded: () => void): void => {
        // TODO 2025.1: future skin for other monitor, it should send the name of new skin
        const deviceName = id === 0 ? this.deviceModel : "";
        console.log("------- updateDeviceSkinController: " + deviceName + "-------");

        this.deviceSkinController = new DeviceSkinController(
            deviceName,
            (data) => {
                // Always call updateSkin, even if there's no skin
                this.transformService.updateSkin(data);
                if (onSkinLoaded) {
                    onSkinLoaded();
                }
            },
            this.ctx as DeviceContext,
            this.deviceContextHandler,
        );
        this.deviceInput.setSkinController(this.deviceSkinController);
    };

    public unsubscribeFromSocketMessages = () => {
        this.connection.clearMessageSubscriptions();
    };

    public getSocket = (): BaseSocket => this.connection.getSocket();

    public getConnection = (): ConnectionBridge => this.connection;

    public setupRecordingOnConnect = (skinConfiguration: BaseMessage) => this.connection.setRecordingSkinConfiguration(skinConfiguration);
}
